/**
 * Cookie Consent — helpers for GDPR-compliant consent management.
 *
 * Reference: site/docs/audits/MAPPITO_WEBSITE_PLAN.md §15.9
 *
 * Rules:
 *  - EU/EEA/UK traffic: analytics is opt-in only
 *  - Default: essential cookies only (no tracking)
 *  - Consent state stored in first-party cookie
 *  - Non-essential trackers blocked before consent
 *  - Consent logic must not break SSR/SEO rendering
 */

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export type ConsentCategory = "essential" | "analytics" | "marketing";

export interface ConsentState {
  /** Essential cookies — always allowed. */
  essential: true;
  /** Analytics cookies (PostHog, GA4). Opt-in for EU. */
  analytics: boolean;
  /** Marketing cookies. Opt-in for EU. */
  marketing: boolean;
  /** ISO timestamp when consent was given/updated. */
  updatedAt: string;
}

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

export const CONSENT_COOKIE_NAME = "mappito_consent";
export const CONSENT_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

/** Default consent state: essential only, no tracking. */
export const DEFAULT_CONSENT: ConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  updatedAt: new Date().toISOString(),
};

/* -------------------------------------------------------------------------- */
/*  Read / Write (client-side)                                                 */
/* -------------------------------------------------------------------------- */

/** Read consent state from cookie. Returns null if no consent given yet. */
export function getConsentState(): ConsentState | null {
  if (typeof document === "undefined") return null;

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, ...rest] = cookie.trim().split("=");
    if (name === CONSENT_COOKIE_NAME) {
      try {
        return JSON.parse(decodeURIComponent(rest.join("="))) as ConsentState;
      } catch {
        return null;
      }
    }
  }
  return null;
}

/** Save consent state to cookie. */
export function setConsentState(state: ConsentState): void {
  if (typeof document === "undefined") return;

  const value = encodeURIComponent(JSON.stringify(state));
  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax; Secure`;
}

/** Check if a specific consent category is allowed. */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === "essential") return true;

  const state = getConsentState();
  if (!state) return false; // No consent banner interaction → block non-essential
  return state[category] ?? false;
}

/** Accept all consent categories. */
export function acceptAllConsent(): ConsentState {
  const state: ConsentState = {
    essential: true,
    analytics: true,
    marketing: true,
    updatedAt: new Date().toISOString(),
  };
  setConsentState(state);
  return state;
}

/** Accept only essential cookies. */
export function acceptEssentialOnly(): ConsentState {
  const state: ConsentState = {
    essential: true,
    analytics: false,
    marketing: false,
    updatedAt: new Date().toISOString(),
  };
  setConsentState(state);
  return state;
}

/** Update specific consent categories. */
export function updateConsent(
  updates: Partial<Pick<ConsentState, "analytics" | "marketing">>,
): ConsentState {
  const current = getConsentState() ?? DEFAULT_CONSENT;
  const state: ConsentState = {
    ...current,
    ...updates,
    essential: true, // always on
    updatedAt: new Date().toISOString(),
  };
  setConsentState(state);
  return state;
}

/** Revoke all non-essential consent. */
export function revokeConsent(): ConsentState {
  return acceptEssentialOnly();
}
